import { useEffect, useState } from "react";
import { FaTimes } from "react-icons/fa";
import api from "../../../../../utils/axiosInstance";
import FormSelect from "../../../../../components/Form/FormSelect";
import { btn } from "../../../../../components/Button/buttonStyles";
import { toast } from "../../../../../components/Toast/toast";
import { formatDateTime } from "../../quotationFormat";

// Lists the client's projects that no quotation links to yet.
const LinkProjectModal = ({ quotationId, onClose, onLinked }) => {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get(`/quotations/${quotationId}/linkable-projects`)
      .then((res) => setProjects(res.data.data || []))
      .catch((err) => {
        console.error(err);
        toast.error("Failed to load projects for this client");
      })
      .finally(() => setLoading(false));
  }, [quotationId]);

  const selected = projects.find((p) => p.id === Number(projectId));

  const handleProceed = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/quotations/${quotationId}/project`, { project_id: Number(projectId) });
      toast.success(res.data.message);
      onLinked(res.data.data);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to link the project");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4">
      <div className="w-full max-w-lg rounded-2xl border border-gray-200 bg-white shadow-pop" role="dialog" aria-modal="true">
        <div className="flex items-center justify-between border-b px-5 py-4">
          <h2 className="text-base font-semibold text-gray-900">Link Project</h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100" aria-label="Close">
            <FaTimes className="text-gray-500" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          {loading ? (
            <div className="flex justify-center py-6">
              <div className="animate-spin rounded-full h-7 w-7 border border-gray-300 border-t-brand-600" />
            </div>
          ) : projects.length === 0 ? (
            <p className="text-sm text-gray-500">
              This client has no projects that are free to link. Every project is either linked to
              another quotation or the client has none yet.
            </p>
          ) : (
            <>
              <FormSelect
                label="Project"
                name="project_id"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                options={projects.map((p) => ({ value: p.id, label: `${p.project_code || "—"} ${p.project_name}` }))}
                placeholder="Select a project"
                required
              />
              {selected && (
                <div className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600 space-y-1">
                  <p>Status: {selected.status}</p>
                  <p>Start: {formatDateTime(selected.start_at) || "—"}</p>
                  <p>Deadline: {formatDateTime(selected.deadline_at) || "—"}</p>
                </div>
              )}
              <p className="text-xs text-gray-500">
                The project's details stay as they are; only the link is saved.
              </p>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-5 py-3 rounded-b-2xl">
          <button onClick={onClose} className={btn("secondary", "sm")}>
            Cancel
          </button>
          <button onClick={handleProceed} disabled={!projectId || saving} className={btn("primary", "sm")}>
            {saving ? "Linking..." : "Proceed"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LinkProjectModal;
